// Module 09 §7 — the shared dashboard CATALOG (the list every surface reads) plus the transient
// cross-workspace view pin. The heavy per-page working copy is NOT here: a dashboard page owns
// its editable rows and persists through `SaveDashboardFlow`.
//
// Ports v3 `stores/dashboardStore.ts` (152 ln) and fixes what the analysis flagged:
//   • raw axios at `:112`/`:132`            → `DashboardApi` (the feature's only http importer)
//   • `workspaceStore.setWsAttributes`/`setWsIntegrations` at `:119-120` → DELETED. A dashboard
//     READ must not mutate workspace state (the island violation ledger A11 exists to kill).
//     07 already maps those two payloads itself (`BootstrapMapper.ts:286-287`) and owns routing
//     them; 09 hands them to nobody. Nothing on `dev` reads them today, so nothing regresses.
//   • `console.error` swallows at `:123`/`:136` → the ApiError propagates to the caller, and a
//     failed load leaves the warm list untouched (v3's "keep what we had" without the silence).
//   • `[key: string]: any` at `:17`         → the typed `DashboardDefinitionDto` (ORP-18656).
//   • `getAccountCenters()` declared with no argument yet called as `getAccountCenters(true)`
//     (`AccountCenterDeleteModal.vue:45`) → `loadAccountCenters` takes a real `force`.
//   • `dashboardAttributes`                  → DROPPED. v3 declares, defaults and resets it and
//     never once writes or reads it (`dashboardStore.ts:24,32,142`) — dead state.
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { dashboardApi, type DashboardApi } from '../api/DashboardApi'
import type { DashboardDefinitionDto, DashboardTemplate } from '../models/api.types'
import { dashboardPersistStorage } from '../storage/dashboardStorage'

/** The ids the definitions fan-out asks for (contract §6). Supplied by the caller — the store
 *  reads no other feature's state, which is what keeps the fan-out testable without mocking 07. */
export interface LoadDefinitionsOptions {
  /** The currently selected workspace; always included when set. */
  workspaceId?: number | string | null
  /** Every `workspace_id` under `accountStore.userAccessInfo.dashboards[].items` (v3 `:107-109`). */
  accessWorkspaceIds?: readonly (number | string | null | undefined)[]
  /** Refetch even when the list is already warm (v3 `getCustomDashboards(force)`). */
  force?: boolean
}

function sameId(a: number | string | null | undefined, b: number | string): boolean {
  return a != null && String(a) === String(b)
}

export const useDashboardStore = defineStore(
  'dashboardStore',
  () => {
    // ── State ────────────────────────────────────────────────────────────
    const definitions = ref<DashboardDefinitionDto[]>([])
    const accountCenters = ref<DashboardDefinitionDto[]>([])
    /** Which workspace's dashboards are currently on screen — a transient pin for account-center
     *  cross-workspace viewing. Cleared on every navigation (§8 `registerDashboardNavReset`), so
     *  persisting it would resurrect a pin the very reset exists to drop. */
    const dashboardWsId = ref<number | string | null>(null)
    const templates = ref<DashboardTemplate[]>([])

    let api: DashboardApi = dashboardApi
    /** Swap the transport. The one seam that makes the fan-out assertable without a network. */
    function setApi(next: DashboardApi): void {
      api = next
    }

    // ── Getters ──────────────────────────────────────────────────────────
    const firstDashboard = computed<DashboardDefinitionDto | null>(
      () => definitions.value[0] ?? null,
    )
    const hasAnyDashboard = computed(() => definitions.value.length > 0)

    function dashboardByUrl(url: string): DashboardDefinitionDto | null {
      return definitions.value.find((d) => d.url_id === url) ?? null
    }
    function dashboardById(id: number | string): DashboardDefinitionDto | null {
      return definitions.value.find((d) => Number(d.id) === Number(id)) ?? null
    }
    function accountCenterByUrl(url: string): DashboardDefinitionDto | null {
      return accountCenters.value.find((d) => d.url_id === url) ?? null
    }
    function accountCenterById(id: number | string): DashboardDefinitionDto | null {
      return accountCenters.value.find((d) => Number(d.id) === Number(id)) ?? null
    }

    // ── Actions ──────────────────────────────────────────────────────────

    /** Adopt a list wholesale — the bootstrap hydrator's entry point (§8). */
    function setDefinitions(next: readonly DashboardDefinitionDto[]): void {
      definitions.value = [...next]
    }

    function setAccountCenters(next: readonly DashboardDefinitionDto[]): void {
      accountCenters.value = [...next]
    }

    /**
     * The per-workspace fan-out (contract §6), ported from v3 `:100-126`. The BOOTSTRAP hydrator
     * is the primary source; this is the explicit migration fallback for when the
     * `dashboard_access` section is absent.
     *
     * Warm-list semantics are v3's: a no-force call with a non-empty list is a no-op, and a
     * failed fetch leaves the previous list standing — but the error now propagates instead of
     * being logged and swallowed.
     */
    async function loadDefinitions(options: LoadDefinitionsOptions = {}): Promise<void> {
      if (definitions.value.length > 0 && !options.force) return

      const wsIds = new Set<number | string>()
      if (options.workspaceId) wsIds.add(options.workspaceId)
      for (const id of options.accessWorkspaceIds ?? []) if (id) wsIds.add(id)
      if (wsIds.size === 0) return

      // Promise.all, not allSettled: v3's fan-out is all-or-nothing, and a partial list would
      // read as "these are all your dashboards" while silently hiding a workspace's worth.
      const responses = await Promise.all([...wsIds].map((id) => api.listDefinitions(id)))
      // `workspace_attributes` / `workspace_integrations` ride these responses and are
      // deliberately dropped here — see the header note on the deleted cross-store write.
      definitions.value = responses.flatMap((body) => body?.data ?? [])
    }

    /** Ported v3 `getAccountCenters` — with the `force` argument its callers already passed. */
    async function loadAccountCenters(
      accountId: number | string | null | undefined,
      force = false,
    ): Promise<void> {
      if (!accountId) return
      if (accountCenters.value.length > 0 && !force) return
      accountCenters.value = await api.listAccountCenters(accountId)
    }

    async function loadTemplates(force = false): Promise<void> {
      if (templates.value.length > 0 && !force) return
      templates.value = await api.listTemplates()
    }

    function setDashboardWsId(id: number | string | null): void {
      dashboardWsId.value = id
    }

    /** Per-navigation reset (v3 `router/index.js:51`), wired via 05's `registerNavReset` (§8). */
    function clearDashboardWs(): void {
      dashboardWsId.value = null
    }

    function mergeById(
      list: readonly DashboardDefinitionDto[],
      definition: DashboardDefinitionDto,
    ): DashboardDefinitionDto[] {
      const index = list.findIndex((d) => Number(d.id) === Number(definition.id))
      if (index < 0) return [...list, definition]
      return list.map((entry, at) => (at === index ? definition : entry))
    }

    /** Adopt one saved definition in place — the post-upsert local patch (§8). */
    function upsertLocal(definition: DashboardDefinitionDto): void {
      definitions.value = mergeById(definitions.value, definition)
    }

    function upsertAccountCenterLocal(definition: DashboardDefinitionDto): void {
      accountCenters.value = mergeById(accountCenters.value, definition)
    }

    function removeLocal(id: number | string): void {
      definitions.value = definitions.value.filter((d) => !sameId(d.id, id))
    }

    function removeAccountCenterLocal(id: number | string): void {
      accountCenters.value = accountCenters.value.filter((d) => !sameId(d.id, id))
    }

    /** Logout / session reset (contract §7). */
    function resetState(): void {
      definitions.value = []
      accountCenters.value = []
      dashboardWsId.value = null
      templates.value = []
    }

    return {
      definitions,
      accountCenters,
      dashboardWsId,
      templates,
      firstDashboard,
      hasAnyDashboard,
      dashboardByUrl,
      dashboardById,
      accountCenterByUrl,
      accountCenterById,
      setDefinitions,
      setAccountCenters,
      loadDefinitions,
      loadAccountCenters,
      loadTemplates,
      setDashboardWsId,
      clearDashboardWs,
      upsertLocal,
      upsertAccountCenterLocal,
      removeLocal,
      removeAccountCenterLocal,
      resetState,
      setApi,
    }
  },
  {
    // The catalog is cached so a reload paints the sidebar before the network answers; the
    // transient pin and the starter templates are re-derived every boot (contract §7).
    //
    // v3 persists this same key with NO pick list, so its blob also carries `dashboardWsId` and
    // the dead `dashboardAttributes`. The two shapes overlap safely in both directions: v3 keys
    // its list `dashboards`, v4 keys it `definitions`, and each app treats a missing/empty list
    // as "not loaded" and refetches. Worst case after crossing apps is one extra fetch.
    persist: {
      key: 'dashboardStore',
      pick: ['definitions', 'accountCenters'],
      storage: dashboardPersistStorage,
    },
  },
)

export type DashboardStore = ReturnType<typeof useDashboardStore>
